/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import {
  toast as baseToast,
  type Id,
  type ToastContent,
  type ToastOptions,
  type UpdateOptions,
} from 'react-toastify';

/**
 * react-toastify v11 renders string content as a bare text node directly inside
 * the toast element — there is no `.Toastify__toast-body` wrapper to style.
 * Alert toasts use a fixed height so react-toastify's `stacked` collapse effect
 * works, so long strings need an element we can line-clamp. This wrapper boxes
 * string content in an `.alert-toast-message` span; the clamp and hover tooltip
 * live in toast.css and useAlertToastTooltips. Non-string (JSX) content — used
 * by the notification toasts — passes through untouched.
 *
 * Import `toast` from here instead of from 'react-toastify' everywhere except the
 * `<ToastContainer>` setup in App.tsx.
 */
function wrapContent<TData>(content: ToastContent<TData>): ToastContent<TData> {
  if (typeof content === 'string') {
    return <span className="alert-toast-message">{content}</span>;
  }
  return content;
}

type ToastMethod = <TData = unknown>(
  content: ToastContent<TData>,
  options?: ToastOptions<TData>,
) => Id;

function wrapMethod(method: ToastMethod): ToastMethod {
  return (content, options) => method(wrapContent(content), options);
}

const wrappedBase = wrapMethod(baseToast as ToastMethod) as typeof baseToast;

export const toast = Object.assign(wrappedBase, baseToast, {
  success: wrapMethod(baseToast.success),
  info: wrapMethod(baseToast.info),
  error: wrapMethod(baseToast.error),
  warning: wrapMethod(baseToast.warning),
  warn: wrapMethod(baseToast.warning),
  loading: wrapMethod(baseToast.loading as ToastMethod) as typeof baseToast.loading,
  update: <TData = unknown>(id: Id, options: UpdateOptions<TData> = {}) =>
    baseToast.update(
      id,
      options.render !== undefined ? { ...options, render: wrapContent(options.render) } : options,
    ),
});

export type { Id, ToastContent, ToastOptions } from 'react-toastify';
